import React, { useState } from 'react';
import { userService } from '../services/firebaseService';

const FakeUserManagementPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('create');
  const [logs, setLogs] = useState<string[]>([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isCreatingMale, setIsCreatingMale] = useState(false);
  const [isAssigningTokens, setIsAssigningTokens] = useState(false);
  const [progress, setProgress] = useState(0);

  const addLog = (message: string) => {
    setLogs(prev => [...prev, `[${new Date().toLocaleTimeString()}] ${message}`]);
  };

  const clearLogs = () => {
    setLogs([]);
    setProgress(0);
  };

  const analyzeUserFormats = async () => {
    if (isAnalyzing) return;

    setIsAnalyzing(true);
    setLogs([]);
    
    try {
      addLog('🔍 Kullanıcı formatları analiz ediliyor...');
      
      // Admin kullanıcı analizi
      addLog('1️⃣ Admin kullanıcı preferences kontrol ediliyor...');
      const adminAnalysis = await (userService as any).analyzeAdminUserPreferences();
      
      if (adminAnalysis.exists) {
        addLog(`✅ Admin kullanıcı bulundu: ${adminAnalysis.data.name}`);
        addLog(`   📧 Email: ${adminAnalysis.data.email}`);
        addLog(`   👤 Gender: ${adminAnalysis.data.gender}`);
        addLog(`   💕 InterestedIn: ${JSON.stringify(adminAnalysis.data.interestedIn)}`);
        addLog(`   🔍 InterestedIn Type: ${adminAnalysis.data.interestedInType}`);
        addLog(`   📋 InterestedIn Array?: ${adminAnalysis.data.interestedInIsArray}`);
        
        // Problem tespit et
        if (!Array.isArray(adminAnalysis.data.interestedIn)) {
          addLog(`❌ SORUN: interestedIn array değil! Flutter arrayContains bekliyor.`);
        }
        
        if (!adminAnalysis.data.interestedIn?.includes('Kadın') || !adminAnalysis.data.interestedIn?.includes('Erkek')) {
          addLog(`❌ SORUN: interestedIn hem Kadın hem Erkek içermiyor!`);
        }
      } else {
        addLog('❌ Admin kullanıcı bulunamadı');
      }
      
      // Erkek kullanıcı analizi
      addLog('2️⃣ Son oluşturulan erkek kullanıcılar kontrol ediliyor...');
      const maleUsers = await (userService as any).analyzeRecentMaleUsers();
      
      if (maleUsers.length > 0) {
        addLog(`✅ ${maleUsers.length} erkek kullanıcı bulundu`);
        const firstMale = maleUsers[0];
        addLog(`   👤 İlk erkek: ${firstMale.name}`);
        addLog(`   👤 Gender: ${firstMale.gender}`);
        addLog(`   💕 InterestedIn: ${JSON.stringify(firstMale.interestedIn)}`);
      } else {
        addLog('❌ Hiç erkek kullanıcı bulunamadı');
      }
      
    } catch (error) {
      addLog(`❌ Analiz hatası: ${error}`);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const fixAdminPreferences = async () => {
    if (isAnalyzing) return;

    setIsAnalyzing(true);
    setLogs([]);
    
    try {
      addLog('🔧 Admin kullanıcının preferences\'ı düzeltiliyor...');
      
      await (userService as any).updateAdminUserPreferences();
      addLog('✅ Admin preferences başarıyla güncellendi!');
      addLog('   👤 Gender: Erkek');
      addLog('   💕 InterestedIn: [Kadın, Erkek]');
      addLog('');
      addLog('🎉 Artık Flutter uygulamasında hem kadın hem erkek kullanıcılar görünmeli!');
      
    } catch (error) {
      addLog(`❌ Admin preferences güncelleme hatası: ${error}`);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const createSupabaseUsers = async () => {
    if (isCreating || isCreatingMale) return;

    setIsCreating(true);
    setProgress(0);
    setLogs([]);

    try {
      addLog('🚀 SUPABASE: 35 adet Türk kadın kullanıcı oluşturma işlemi başlatılıyor...');
      
      // Supabase service'ini import et
      const { supabaseService } = await import('../services/supabaseService');
      
      const result = await supabaseService.create100TurkishWomen((current: number, total: number, message: string) => {
        setProgress(Math.round((current / total) * 100));
        addLog(message);
      });

      if (result.success) {
        addLog(`✅ İşlem başarıyla tamamlandı!`);
        addLog(`📊 Toplam oluşturulan kullanıcı: ${result.createdCount}`);
        addLog(`⚠️ Hata sayısı: ${result.errorCount}`);
      } else {
        addLog(`❌ İşlem başarısız oldu: ${result.message}`);
      }

    } catch (error: any) {
      addLog(`❌ Kritik hata: ${error.message}`);
      console.error('Supabase user creation error:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const createSupabaseMaleUsers = async () => {
    if (isCreating || isCreatingMale) return;

    setIsCreatingMale(true);
    setProgress(0);
    setLogs([]);

    try {
      addLog('🚀 SUPABASE: 100 adet Türk erkek kullanıcı oluşturma işlemi başlatılıyor...');
      
      // Supabase service'ini import et
      const { supabaseService } = await import('../services/supabaseService');
      
      const result = await supabaseService.create100TurkishMen((current: number, total: number, message: string) => {
        setProgress(Math.round((current / total) * 100));
        addLog(message);
      });

      if (result.success) {
        addLog(`✅ İşlem başarıyla tamamlandı!`);
        addLog(`📊 Toplam oluşturulan kullanıcı: ${result.createdCount}`);
        addLog(`⚠️ Hata sayısı: ${result.errorCount}`);
      } else {
        addLog(`❌ İşlem başarısız oldu: ${result.message}`);
      }

    } catch (error: any) {
      addLog(`❌ Kritik hata: ${error.message}`);
      console.error('Supabase male user creation error:', error);
    } finally {
      setIsCreatingMale(false);
    }
  };

  const assignFCMTokens = async () => {
    if (isCreating || isCreatingMale || isAssigningTokens || isAnalyzing) return;

    setIsAssigningTokens(true);
    setProgress(0);
    setLogs([]);

    try {
      addLog('📱 FCM Token Atama: FCM token\'ı boş olan kullanıcılara benzersiz token atama işlemi başlatılıyor...');
      
      const result = await (userService as any).assignFCMTokensToEmptyUsers((current: number, total: number, message: string) => {
        setProgress(Math.round((current / total) * 100));
        addLog(message);
      });

      if (result.success) {
        addLog(`✅ FCM Token atama işlemi başarıyla tamamlandı!`);
        addLog(`📊 Toplam güncellenen kullanıcı: ${result.updatedCount}`);
        addLog(`📊 Toplam bulunan kullanıcı: ${result.totalCount}`);
        addLog(`⚠️ Hata sayısı: ${result.errorCount || 0}`);
        
        if (result.updatedCount === 0) {
          addLog(`ℹ️ Tüm kullanıcıların FCM token'ı zaten mevcut!`);
        }
      } else {
        addLog(`❌ FCM Token atama işlemi başarısız oldu: ${result.message}`);
      }

    } catch (error: any) {
      addLog(`❌ FCM Token atama kritik hatası: ${error.message}`);
      console.error('FCM Token assignment error:', error);
    } finally {
      setIsAssigningTokens(false);
    }
  };

  const TabButton = ({ id, label, icon }: { id: string; label: string; icon: string }) => (
    <button
      onClick={() => setActiveTab(id)}
      style={{
        padding: '12px 24px',
        backgroundColor: activeTab === id ? '#28a745' : '#f8f9fa',
        color: activeTab === id ? 'white' : '#495057',
        border: '1px solid #dee2e6',
        borderRadius: '8px 8px 0 0',
        cursor: 'pointer',
        fontSize: '16px',
        fontWeight: 'bold',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        transition: 'all 0.3s ease',
        borderBottom: activeTab === id ? 'none' : '1px solid #dee2e6'
      }}
    >
      <span>{icon}</span>
      <span>{label}</span>
    </button>
  );

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      <h1 style={{ color: '#2c3e50', marginBottom: '10px' }}>
        👥 Fake Kullanıcı Yönetimi
      </h1>
      <p style={{ color: '#7f8c8d', marginBottom: '30px' }}>
        Bu sayfa sahte Türk kadın kullanıcı profilleri oluşturmak ve yönetmek için kullanılır.
      </p>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        borderBottom: '1px solid #dee2e6',
        marginBottom: '30px'
      }}>
        <TabButton 
          id="create" 
          label="Sistem Yönetimi" 
          icon="🔧" 
        />
      </div>

      {/* Tab Content */}
        {activeTab === 'create' && (
          <div>
            {/* Create Users Tab Content */}
            <div style={{
              backgroundColor: '#f8f9fa',
              padding: '25px',
              borderRadius: '12px',
              marginBottom: '30px',
              border: '1px solid #e9ecef'
            }}>
              <h3 style={{ color: '#495057', marginBottom: '20px', display: 'flex', alignItems: 'center' }}>
              🔧 Sistem Yönetim Paneli
              </h3>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '20px',
                marginBottom: '25px'
              }}>
              {/* Supabase Kadın Kullanıcı Oluşturma */}
                <div style={{
                  padding: '20px',
                  backgroundColor: '#fff',
                  borderRadius: '8px',
                  border: '1px solid #dee2e6',
                  textAlign: 'center'
                }}>
                <h4 style={{ color: '#28a745', margin: '0 0 15px 0' }}>👩 Supabase Kadın Kullanıcı</h4>
                  <p style={{ fontSize: '14px', color: '#6c757d', marginBottom: '15px' }}>
                  35 adet Türk kadın kullanıcı oluşturur (Supabase DB)
                  </p>
                  <button
                  onClick={createSupabaseUsers}
                  disabled={isCreating || isCreatingMale || isAnalyzing || isAssigningTokens}
                    style={{
                    backgroundColor: (isCreating || isCreatingMale || isAnalyzing || isAssigningTokens) ? '#6c757d' : '#28a745',
                      color: 'white',
                      border: 'none',
                      padding: '12px 24px',
                      borderRadius: '6px',
                    cursor: (isCreating || isCreatingMale || isAnalyzing) ? 'not-allowed' : 'pointer',
                      fontSize: '16px',
                      fontWeight: 'bold',
                    width: '100%',
                    marginBottom: '10px'
                    }}
                  >
                  {isCreating ? '⏳ Oluşturuluyor...' : '🚀 35 Kadın Oluştur'}
                  </button>
                {/* İlerleme Çubuğu */}
                {isCreating && (
                  <div style={{ marginTop: '10px' }}>
                  <div style={{
                    backgroundColor: '#e9ecef',
                    borderRadius: '10px',
                    height: '20px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      backgroundColor: '#28a745',
                      height: '100%',
                      width: `${progress}%`,
                      transition: 'width 0.3s ease',
                      borderRadius: '10px'
                    }} />
                  </div>
                    <p style={{ margin: '5px 0 0 0', fontSize: '12px', color: '#495057' }}>
                    %{progress} Tamamlandı
                  </p>
                  </div>
                )}
                </div>

              {/* Supabase Erkek Kullanıcı Oluşturma */}
                <div style={{
                  padding: '20px',
                  backgroundColor: '#fff',
                  borderRadius: '8px',
                  border: '1px solid #dee2e6',
                  textAlign: 'center'
                }}>
                <h4 style={{ color: '#007bff', margin: '0 0 15px 0' }}>👨 Supabase Erkek Kullanıcı</h4>
                  <p style={{ fontSize: '14px', color: '#6c757d', marginBottom: '15px' }}>
                  100 adet Türk erkek kullanıcı oluşturur (Supabase DB)
                  </p>
                  <button
                  onClick={createSupabaseMaleUsers}
                  disabled={isCreating || isCreatingMale || isAnalyzing || isAssigningTokens}
                    style={{
                    backgroundColor: (isCreating || isCreatingMale || isAnalyzing || isAssigningTokens) ? '#6c757d' : '#007bff',
                      color: 'white',
                      border: 'none',
                      padding: '12px 24px',
                      borderRadius: '6px',
                    cursor: (isCreating || isCreatingMale || isAnalyzing || isAssigningTokens) ? 'not-allowed' : 'pointer',
                      fontSize: '16px',
                      fontWeight: 'bold',
                    width: '100%',
                    marginBottom: '10px'
                    }}
                  >
                  {isCreatingMale ? '⏳ Oluşturuluyor...' : '🚀 100 Erkek Oluştur'}
                  </button>
                {/* İlerleme Çubuğu */}
                {isCreatingMale && (
                  <div style={{ marginTop: '10px' }}>
                    <div style={{
                      backgroundColor: '#e9ecef',
                      borderRadius: '10px',
                      height: '20px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        backgroundColor: '#007bff',
                        height: '100%',
                        width: `${progress}%`,
                        transition: 'width 0.3s ease',
                        borderRadius: '10px'
                      }} />
                    </div>
                    <p style={{ margin: '5px 0 0 0', fontSize: '12px', color: '#495057' }}>
                      %{progress} Tamamlandı
                    </p>
                  </div>
                )}
                </div>

                {/* Analyze Formats */}
                <div style={{
                  padding: '20px',
                  backgroundColor: '#fff',
                  borderRadius: '8px',
                  border: '1px solid #dee2e6',
                  textAlign: 'center'
                }}>
                  <h4 style={{ color: '#6f42c1', margin: '0 0 15px 0' }}>🔍 Analiz</h4>
                  <p style={{ fontSize: '14px', color: '#6c757d', marginBottom: '15px' }}>
                    Kullanıcı formatlarını ve uyumluluğu kontrol eder
                  </p>
                  <button
                    onClick={analyzeUserFormats}
                  disabled={isAnalyzing || isCreating || isCreatingMale || isAssigningTokens}
                    style={{
                    backgroundColor: (isAnalyzing || isCreating || isCreatingMale || isAssigningTokens) ? '#6c757d' : '#6f42c1',
                      color: 'white',
                      border: 'none',
                      padding: '12px 24px',
                      borderRadius: '6px',
                    cursor: (isAnalyzing || isCreating || isCreatingMale) ? 'not-allowed' : 'pointer',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      width: '100%'
                    }}
                  >
                    {isAnalyzing ? '⏳ Analiz Ediliyor...' : '🔍 Format Analizi'}
                  </button>
                </div>

                {/* Admin Preferences Düzeltme */}
                <div style={{
                  padding: '20px',
                  backgroundColor: '#fff',
                  borderRadius: '8px',
                  border: '1px solid #dee2e6',
                  textAlign: 'center'
                }}>
                  <h4 style={{ color: '#dc3545', margin: '0 0 15px 0' }}>🔧 Admin Düzeltme</h4>
                  <p style={{ fontSize: '14px', color: '#6c757d', marginBottom: '15px' }}>
                    Admin kullanıcının preferences'ını Flutter uyumlu formata günceller
                  </p>
                  <button
                    onClick={fixAdminPreferences}
                    disabled={isAnalyzing || isCreating || isCreatingMale || isAssigningTokens}
                    style={{
                      backgroundColor: (isAnalyzing || isCreating || isCreatingMale || isAssigningTokens) ? '#6c757d' : '#dc3545',
                      color: '#fff',
                      border: 'none',
                      padding: '12px 24px',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      cursor: (isAnalyzing || isCreating || isCreatingMale || isAssigningTokens) ? 'not-allowed' : 'pointer',
                      minWidth: '200px',
                      transition: 'all 0.3s ease',
                    marginBottom: '10px',
                    width: '100%'
                    }}
                  >
                    {isAnalyzing ? '⏳ Düzeltiliyor...' : '🔧 Admin Düzelt'}
                  </button>
              </div>

              {/* FCM Token Atama */}
              <div style={{
                padding: '20px',
                backgroundColor: '#fff',
                borderRadius: '8px',
                border: '1px solid #dee2e6',
                textAlign: 'center'
              }}>
                <h4 style={{ color: '#ff9800', margin: '0 0 15px 0' }}>📱 FCM Token Atama</h4>
                <p style={{ fontSize: '14px', color: '#6c757d', marginBottom: '15px' }}>
                  FCM token'ı boş olan kullanıcılara benzersiz token atar
                </p>
                <button
                  onClick={assignFCMTokens}
                  disabled={isCreating || isCreatingMale || isAnalyzing || isAssigningTokens}
                  style={{
                    backgroundColor: (isCreating || isCreatingMale || isAnalyzing || isAssigningTokens) ? '#6c757d' : '#ff9800',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '6px',
                    cursor: (isCreating || isCreatingMale || isAnalyzing || isAssigningTokens) ? 'not-allowed' : 'pointer',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    width: '100%',
                    marginBottom: '10px'
                  }}
                >
                  {isAssigningTokens ? '⏳ Token Atanıyor...' : '📱 FCM Token Ata'}
                </button>
                {/* İlerleme Çubuğu */}
                {isAssigningTokens && (
                  <div style={{ marginTop: '10px' }}>
                    <div style={{
                      backgroundColor: '#e9ecef',
                      borderRadius: '10px',
                      height: '20px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        backgroundColor: '#ff9800',
                        height: '100%',
                        width: `${progress}%`,
                        transition: 'width 0.3s ease',
                        borderRadius: '10px'
                      }} />
                    </div>
                    <p style={{ margin: '5px 0 0 0', fontSize: '12px', color: '#495057' }}>
                      %{progress} Tamamlandı
                    </p>
                  </div>
                )}
              </div>

              {/* Logları Temizle */}
              <div style={{
                padding: '20px',
                backgroundColor: '#fff',
                borderRadius: '8px',
                border: '1px solid #dee2e6',
                textAlign: 'center'
              }}>
                <h4 style={{ color: '#dc3545', margin: '0 0 15px 0' }}>🧹 Temizlik</h4>
                <p style={{ fontSize: '14px', color: '#6c757d', marginBottom: '15px' }}>
                  Konsol loglarını ve ilerlemeyi temizler
                </p>
                <button
                  onClick={clearLogs}
                  disabled={isCreating || isCreatingMale || isAnalyzing || isAssigningTokens}
                  style={{
                    backgroundColor: (isCreating || isCreatingMale || isAnalyzing || isAssigningTokens) ? '#6c757d' : '#dc3545',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '6px',
                    cursor: (isCreating || isCreatingMale || isAnalyzing || isAssigningTokens) ? 'not-allowed' : 'pointer',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    width: '100%'
                  }}
                >
                  🗑️ Logları Temizle
                </button>
                </div>
              </div>
            </div>

            {/* Console Output */}
            <div style={{
              backgroundColor: '#1e1e1e',
              padding: '20px',
              borderRadius: '12px',
              border: '1px solid #343a40'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '15px'
              }}>
                <h3 style={{ color: '#28a745', margin: 0, fontFamily: 'monospace' }}>
                  💻 İşlem Konsolu
                </h3>
              </div>

              <div style={{
                backgroundColor: '#000',
                padding: '15px',
                borderRadius: '8px',
                height: '400px',
                overflowY: 'auto',
                fontFamily: 'monospace',
                fontSize: '14px',
                lineHeight: '1.5'
              }}>
                {logs.length === 0 ? (
                  <div style={{ color: '#6c757d', fontStyle: 'italic' }}>
                    Konsol çıktıları burada görünecek...
                  </div>
                ) : (
                  logs.map((log, index) => (
                    <div key={index} style={{
                      color: log.includes('✅') ? '#28a745' :
                            log.includes('❌') ? '#dc3545' :
                            log.includes('⚠️') ? '#ffc107' :
                            log.includes('📊') ? '#17a2b8' :
                            '#28a745',
                      marginBottom: '5px'
                    }}>
                      {log}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default FakeUserManagementPage; 